import { collection, getDocs, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getUserCountry, getPricingPlans } from './countryPricing';
import { auth } from '../firebaseConfig';

/**
 * Fetch all plan bundles with country-aware pricing
 * @returns {Promise<Array>} Array of plan bundle objects with id and data
 */
export const getAllPlanBundles = async () => {
  try {
    // Get user's country first
    let userCountry = 'US'; // Default fallback
    if (auth.currentUser) {
      try {
        userCountry = await getUserCountry(auth.currentUser.uid);
      } catch (error) {
        console.warn('Could not get user country, using US as fallback:', error);
      }
    }

    // Get all plans for the user's country
    const forwardingMonthly = getPricingPlans(userCountry, 'forwarding', 'monthly');
    const forwardingAnnual = getPricingPlans(userCountry, 'forwarding', 'annual');
    const messaging = getPricingPlans(userCountry, 'messaging', 'quarterly');

    // Convert to the format expected by SubscriptionUpgradePanel
    const allPlans = [...forwardingMonthly, ...forwardingAnnual, ...messaging];
    
    return allPlans.map(plan => ({
      id: plan.bundleId,
      priceId: plan.id,
      name: plan.name,
      lines: plan.lines,
      period: plan.period,
      isMessaging: plan.isMessaging,
      displayPrice: plan.price,
      displayPricePerLine: plan.pricePerLine,
      hasTrial: plan.hasTrial || false,
      savings: plan.savings || null,
      country: plan.country
    }));

  } catch (error) {
    console.error('Error fetching plan bundles:', error);
    
    // Fallback to Firestore if country pricing fails
    try {
      const planBundlesRef = collection(db, 'planBundles');
      const snapshot = await getDocs(planBundlesRef);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (firestoreError) {
      console.error('Firestore fallback also failed:', firestoreError);
      throw error;
    }
  }
};

/**
 * Fetch a specific plan bundle by ID
 * @param {string} bundleId - The bundle ID (e.g., 'Bundle1LinesMonth')
 * @returns {Promise<Object|null>} Plan bundle object or null if not found
 */
export const getPlanBundle = async (bundleId) => {
  try {
    const bundleRef = doc(db, 'planBundles', bundleId);
    const snapshot = await getDoc(bundleRef);
    
    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    }
    return null;
  } catch (error) {
    console.error(`Error fetching plan bundle ${bundleId}:`, error);
    throw error;
  }
};

/**
 * Fetch plan bundles filtered by period (Month, Annual, Quarterly)
 * @param {string} period - The billing period to filter by
 * @returns {Promise<Array>} Array of plan bundle objects
 */
export const getPlanBundlesByPeriod = async (period) => {
  try {
    const planBundlesRef = collection(db, 'planBundles');
    const q = query(
      planBundlesRef,
      where('period', '==', period),
      orderBy('lines', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error fetching plan bundles for period ${period}:`, error);
    throw error;
  }
};

/**
 * Fetch plan bundles filtered by messaging capability
 * @param {boolean} hasMessaging - Whether to get messaging or non-messaging bundles
 * @returns {Promise<Array>} Array of plan bundle objects
 */
export const getPlanBundlesByMessaging = async (hasMessaging = false) => {
  try {
    const planBundlesRef = collection(db, 'planBundles');
    const q = query(
      planBundlesRef,
      where('isMessaging', '==', hasMessaging),
      orderBy('lines', 'asc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error fetching plan bundles with messaging=${hasMessaging}:`, error);
    throw error;
  }
};

/**
 * Fetch plan bundles filtered by number of lines
 * @param {number} lineCount - The number of lines to filter by
 * @returns {Promise<Array>} Array of plan bundle objects
 */
export const getPlanBundlesByLines = async (lineCount) => {
  try {
    const planBundlesRef = collection(db, 'planBundles');
    const q = query(
      planBundlesRef,
      where('lines', '==', lineCount)
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error fetching plan bundles for ${lineCount} lines:`, error);
    throw error;
  }
};

/**
 * Get formatted pricing table data grouped by period
 * @returns {Promise<Object>} Object with periods as keys and bundles as values
 */
export const getFormattedPricingTable = async () => {
  try {
    const allBundles = await getAllPlanBundles();
    
    const grouped = allBundles.reduce((acc, bundle) => {
      const period = bundle.period;
      if (!acc[period]) {
        acc[period] = [];
      }
      acc[period].push(bundle);
      return acc;
    }, {});

    // Sort each period group by lines count
    Object.keys(grouped).forEach(period => {
      grouped[period].sort((a, b) => a.lines - b.lines);
    });

    return grouped;
  } catch (error) {
    console.error('Error getting formatted pricing table:', error);
    throw error;
  }
}; 